import React from 'react'
import {
  HistoryOutlined,
  LayoutOutlined,
  MobileOutlined,
  ScanOutlined,
  TeamOutlined,
} from '@ant-design/icons'
import { GrDocumentUser } from 'react-icons/gr'
import { RxDashboard, RxEnvelopeClosed } from 'react-icons/rx'

/** A single navigation entry in the event sidebar menu tree. */
export interface MenuItem {
  key: string
  label: string
  icon?: React.ReactNode
  children?: MenuItem[]
  path?: string
}

/**
 * Event admin sidebar menu tree.
 *
 * Shared between `Sidebar` (which renders the navigation) and `EventLayout`
 * (which derives the document title from the active section) so the
 * url -> section-label mapping has a single source of truth.
 */
export const defaultMenuItems: MenuItem[] = [
  {
    key: 'dashboard',
    label: 'Dashboard',
    icon: <RxDashboard />,
    path: 'show',
  },
  {
    key: 'event-settings',
    label: 'Event Settings',
    icon: <LayoutOutlined />,
    path: 'settings',
  },
  {
    key: 'registration',
    label: 'Registration',
    icon: <GrDocumentUser />,
    children: [
      {
        key: 'attendee-management',
        label: 'Attendee Management',
        path: 'attendees',
      },
      {
        key: 'registration-form',
        label: 'Form Fields',
        path: 'registration_form',
      },
      {
        key: 'thank-you-page',
        label: 'Thank You Page',
        path: 'thank_you_page',
      },
      {
        key: 'badge-printing',
        label: 'Badge Printing',
        path: 'badge_printings',
      },
    ],
  },
  {
    key: 'messaging',
    label: 'Messaging',
    icon: <RxEnvelopeClosed />,
    children: [
      {
        key: 'push-notifications',
        label: 'Push Notifications',
        path: 'messaging/push_notifications',
      },
      {
        key: 'email-blast',
        label: 'Email Blast',
        path: 'messaging/email_blasts',
      },
      {
        key: 'whatsapp-blast',
        label: 'WhatsApp Blast',
        path: 'messaging/whatsapp_blasts',
      },
    ],
  },
  {
    key: 'attendee-app',
    label: 'Attendee App',
    icon: <MobileOutlined />,
    children: [
      {
        key: 'agenda-builder',
        label: 'Agenda Builder',
        path: 'agenda',
      },
      {
        key: 'informational-tabs',
        label: 'Informational Tabs',
        path: 'informational-tabs',
      },
      {
        key: 'photo-gallery',
        label: 'Photo Gallery',
        path: 'photo_gallery',
      },
      {
        key: 'lucky-draw',
        label: 'Lucky Draw',
        path: 'lucky_draw',
      },
    ],
  },
  {
    key: 'scan-points',
    label: 'Scan Points',
    icon: <ScanOutlined />,
    path: 'scan_points',
  },
  {
    key: 'user-management',
    label: 'User Management',
    icon: <TeamOutlined />,
    path: 'user_management',
  },
  {
    key: 'activity-logs',
    label: 'Activity Logs',
    icon: <HistoryOutlined />,
    path: 'activity_logs',
  },
]

/** Result of resolving the current URL against the menu tree. */
export interface ActiveMenuResult {
  activeKey: string | null
  activeLabel: string | null
  parentKeys: string[]
  matchLength: number
}

// Checks whether `currentPath` matches `menuPath` exactly or as a path prefix.
const isPathMatch = (currentPath: string, menuPath: string): boolean => {
  // Exact match
  if (currentPath === menuPath || currentPath === menuPath + '/') {
    return true
  }
  // Prefix match - must be followed by '/' to avoid /admin matching /admin/events
  if (currentPath.startsWith(menuPath + '/')) {
    return true
  }
  return false
}

/**
 * Walks the menu tree to find the item whose path best matches `currentPath`.
 * Uses the longest match so more specific paths win over shorter ones.
 */
export const findActiveMenuAndParents = (
  items: MenuItem[],
  currentPath: string,
  slug: string,
  parentKeys: string[] = [],
): ActiveMenuResult => {
  let bestMatch: ActiveMenuResult = {
    activeKey: null,
    activeLabel: null,
    parentKeys: [],
    matchLength: 0,
  }

  for (const item of items) {
    if (item.path) {
      const fullPath = item.path.startsWith('/') ? item.path : `/events/${slug}/${item.path}`

      if (isPathMatch(currentPath, fullPath)) {
        // Keep track of the longest matching path
        if (fullPath.length > bestMatch.matchLength) {
          bestMatch = {
            activeKey: item.key,
            activeLabel: item.label,
            parentKeys,
            matchLength: fullPath.length,
          }
        }
      }
    }

    if (item.children && item.children.length > 0) {
      const result = findActiveMenuAndParents(item.children, currentPath, slug, [
        ...parentKeys,
        item.key,
      ])
      if (result.activeKey && result.matchLength > bestMatch.matchLength) {
        bestMatch = result
      }
    }
  }
  return bestMatch
}

/**
 * Returns the human-readable label of the active section for the given URL, or
 * `null` when no menu item matches. Used by layouts to build the document title.
 */
export const getSectionLabelFromUrl = (
  url: string,
  slug: string,
  items: MenuItem[] = defaultMenuItems,
): string | null => findActiveMenuAndParents(items, url, slug).activeLabel
